import { CameraIcon, PictureIcon, TitleText } from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import React, { useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import { get } from 'lodash'

import {
  ALLOWED_IMAGE_TYPES,
  coverImageErrorCodeMapping,
} from '../../../constants'
import { useLocalizedContent } from '../../../hooks/useLocalizedContent'
import {
  imageFileReader,
  readAsDataURLAsync,
} from '../../../utils/image/image-file-reader'
import {
  dragFocus,
  dragOver,
  dragReject,
  imageCardUploadLabel,
  baseImageCardUpload,
  uploadPhotoCard,
  imageCardUploadVariants,
  imageCardUploadLabelVariants,
  uploadPhotoContentContainer,
} from './ImageUploadField.css'
import { classNames } from '../../../utils/classNames'
import { isCoverOnCreateFlowEnabled } from '../../../utils/features'
import { uploadButton } from '../coverImage.css'

interface ImageUploadProps {
  onChange: (data: any) => void
  onError: Function
  restrictedTotalSize: number
  setProposedPhoto: (proposedPhoto: any) => void
}

function ImageUploadField(props: ImageUploadProps) {
  const { onChange, onError, restrictedTotalSize, setProposedPhoto } = props
  const { getContent } = useLocalizedContent()

  const handleDragDrop = async (files: File[], evt: any) => {
    const error = get(evt, '0.errors.0')
    if (error) {
      return onError(new Error(getContent('coverImage.upload.error')))
    }

    if (isCoverOnCreateFlowEnabled()) {
      const fileReadData: any = await readAsDataURLAsync(files[0])
      const url = fileReadData.data as string
      return setProposedPhoto({ url, name: files[0].name, file: files[0] })
    }

    try {
      const fileHandle = await imageFileReader({ files, restrictedTotalSize })
      return onChange(fileHandle)
    } catch (err) {
      const errorMessage = coverImageErrorCodeMapping[err.code]
      if (errorMessage) {
        return onError(new Error(getContent(errorMessage)))
      }
      return onError(err)
    }
  }

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: ALLOWED_IMAGE_TYPES,
      noDragEventsBubbling: true,
      multiple: false,
      onDrop: handleDragDrop,
    })

  const className = useMemo(() => {
    const imageCardUploadVariant = {
      [imageCardUploadVariants.default]: !isCoverOnCreateFlowEnabled(),
      [imageCardUploadVariants.coverOnCreateFlow]: isCoverOnCreateFlowEnabled(),
    }
    if (isFocused) {
      return classNames(dragFocus, imageCardUploadVariant)
    }
    if (isDragReject) {
      return classNames(dragReject, imageCardUploadVariant)
    }
    if (isDragAccept) {
      return classNames(dragOver, imageCardUploadVariant)
    }
    return classNames(baseImageCardUpload, imageCardUploadVariant)
  }, [isFocused, isDragAccept, isDragReject])

  return (
    <div {...getRootProps({ className })}>
      <div className={uploadPhotoCard}>
        <div data-testid="image-file-upload">
          <div
            className={classNames(imageCardUploadLabel, {
              [imageCardUploadLabelVariants.default]:
                !isCoverOnCreateFlowEnabled(),
              [imageCardUploadLabelVariants.coverOnCreateFlow]:
                isCoverOnCreateFlowEnabled(),
            })}
            data-testid="image-upload-label"
          >
            <div
              {...(isCoverOnCreateFlowEnabled() && {
                className: uploadPhotoContentContainer,
              })}
            >
              {isCoverOnCreateFlowEnabled() ? (
                <CameraIcon size="sm" />
              ) : (
                <PictureIcon color="sysColorSecondaryMain" />
              )}

              <div>
                <label
                  htmlFor="select-image"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isCoverOnCreateFlowEnabled() ? (
                    <TitleText
                      as="span"
                      data-testid="uploadPhotoButtonTitle"
                      className={uploadButton}
                      type="strong"
                    >
                      <Message id="coverImage.upload.button" />
                    </TitleText>
                  ) : (
                    <Message id="imageUploadField.file.upload.selector" />
                  )}
                </label>
                <input
                  {...getInputProps()}
                  data-testid="select-image"
                  id="select-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageUploadField
